/**
 * The native Windows kiosk shell (WebView2), when the app runs inside it (spec
 * docs/specs/2026-10-13-windows-kiosk-shell-design.md §9, protocol v1). The shell hosts the kiosk
 * surface full-screen, owns every extra window (payment pages), and wipes the session between
 * customers; this module is the only place that talks to it.
 *
 * Out: `window.chrome.webview.postMessage({ v: 1, type, ... })`.
 * In: the shell's `PostWebMessageAsJson`, as `message` events on `window.chrome.webview`;
 * `activity` and `windowClosed` become window events (KIOSK_HOST_*_EVENT) that existing hooks
 * listen to. In a normal browser there is no `chrome.webview`: every function is a no-op.
 */

export const KIOSK_HOST_PROTOCOL_VERSION = 1;
/** Sent as `hello.appVersion`: which web client is talking. */
export const KIOSK_WEB_CLIENT = 'kiosk-web-v2';
/** The customer touched a shell window (e.g. a payment page): counts as activity in the app. */
export const KIOSK_HOST_ACTIVITY_EVENT = 'kiosk-host-activity';
/** A shell window closed: `detail` is KioskHostWindowClosed. A UI event, never a payment result. */
export const KIOSK_HOST_WINDOW_CLOSED_EVENT = 'kiosk-host-window-closed';

export type HostWindowPurpose = 'payment' | 'other';

export interface KioskHostInfo {
  shellVersion: string;
  deviceId: string;
  capabilities: string[];
}

export interface KioskHostWindowClosed {
  id: string;
  purpose: HostWindowPurpose;
  reason: string;
}

interface KioskWebView {
  postMessage: (message: unknown) => void;
  addEventListener: (type: 'message', listener: (event: { data: unknown }) => void) => void;
  removeEventListener: (type: 'message', listener: (event: { data: unknown }) => void) => void;
}

declare global {
  interface Window {
    chrome?: { webview?: KioskWebView };
  }
}

let started = false;
let hostInfo: KioskHostInfo | null = null;
let lastSession: boolean | null = null;

function webview(): KioskWebView | null {
  const view = typeof window === 'undefined' ? undefined : window.chrome?.webview;
  return view && typeof view.postMessage === 'function' ? view : null;
}

function post(type: string, fields: Record<string, unknown> = {}): boolean {
  const view = webview();
  if (!view) return false;
  try {
    view.postMessage({ v: KIOSK_HOST_PROTOCOL_VERSION, type, ...fields });
    return true;
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function onHostMessage(event: { data: unknown }): void {
  const data = event?.data;
  if (!isRecord(data) || data.v !== KIOSK_HOST_PROTOCOL_VERSION) return;
  switch (data.type) {
    case 'host':
      hostInfo = {
        shellVersion: typeof data.shellVersion === 'string' ? data.shellVersion : '',
        deviceId: typeof data.deviceId === 'string' ? data.deviceId : '',
        capabilities: Array.isArray(data.capabilities)
          ? data.capabilities.filter((c): c is string => typeof c === 'string')
          : [],
      };
      return;
    case 'activity':
      window.dispatchEvent(new Event(KIOSK_HOST_ACTIVITY_EVENT));
      return;
    case 'windowClosed': {
      const detail: KioskHostWindowClosed = {
        id: typeof data.id === 'string' ? data.id : '',
        purpose: data.purpose === 'payment' ? 'payment' : 'other',
        reason: typeof data.reason === 'string' ? data.reason : '',
      };
      window.dispatchEvent(new CustomEvent<KioskHostWindowClosed>(KIOSK_HOST_WINDOW_CLOSED_EVENT, { detail }));
      return;
    }
    default:
      // windowOpened / windowRefused / resetting: nothing to do (the shell reloads after resetting).
      return;
  }
}

/** True inside the kiosk shell (WebView2 exposes `window.chrome.webview`). */
export function hasNativeHost(): boolean {
  return webview() !== null;
}

/** Listen to the shell and say hello. Safe to call any number of times. */
export function startKioskHost(): void {
  const view = webview();
  if (!view || started) return;
  started = true;
  view.addEventListener('message', onHostMessage);
  post('hello', { appVersion: KIOSK_WEB_CLIENT });
}

/** The shell's answer to `hello`; null until it arrives (or outside the shell). */
export function getHostInfo(): KioskHostInfo | null {
  return hostInfo;
}

/** The shell said it opens windows itself (`openWindow`); until then the app uses window.open. */
export function canHostOpenWindows(): boolean {
  return hasNativeHost() && (hostInfo?.capabilities.includes('openWindow') ?? false);
}

/** Signed in / out. Only changes are sent (token refreshes re-announce the same state). */
export function sendSession(signedIn: boolean): void {
  if (!hasNativeHost() || lastSession === signedIn) return;
  if (post('session', { state: signedIn ? 'signed-in' : 'signed-out' })) lastSession = signedIn;
}

/** A payment is pending: the shell holds its idle reset while `on`. */
export function sendHold(on: boolean, reason: string): void {
  post('hold', { on, reason });
}

/** Ask the shell to open `url` in one of its windows. False outside the shell. */
export function openHostWindow(url: string, purpose: HostWindowPurpose, title?: string): boolean {
  return post('openWindow', title ? { url, purpose, title } : { url, purpose });
}

export function sendCloseWindows(): void {
  post('closeWindows');
}

export function sendHeartbeat(route: string, rendered: boolean): void {
  post('heartbeat', { route, rendered });
}

export function sendLocale(lang: string): void {
  post('locale', { lang });
}

/** Test helper: forget the listener, the host reply and the last session state. */
export function __resetKioskHostForTests(): void {
  const view = webview();
  if (view && started) view.removeEventListener('message', onHostMessage);
  started = false;
  hostInfo = null;
  lastSession = null;
}
